import { useMemo } from "react";
import { MailIcon } from "lucide-react";
import { PencilIcon, PlusIcon, TrashIcon } from "@heroicons/react/24/outline";
import { SupplierRecord } from "@/app/Suppliers/GraphQL/supplierRecordQuery";
import { useDialog } from "@/lib/Components/Dialog/Hooks/useDialog";
import { ConfirmDeleteDialog } from "@/lib/Components/Dialog/ConfirmDeleteDialog";
import { SupplierNotificationEmailDialog } from "@/app/Suppliers/Components/SupplierNotificationEmailDialog";
import { useGqlMutation } from "@/lib/GraphQLCodegen/fetcher";
import { updateSupplierMutation } from "@/app/Suppliers/GraphQL/supplierMutations";
import {
  SupplierNotificationSettingEvent,
  SupplierNotificationSettingType,
} from "@/gql/graphql";
import { Badge } from "@/components/catalyst/badge";
import { Button } from "@/components/catalyst/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useSupplierNotificationEvents } from "@/app/Suppliers/Hooks/useSupplierNotificationEvents";

const MAX_VISIBLE_BADGES = 3;

type EmailGroup = {
  email: string;
  entries: {
    id: string;
    event: SupplierNotificationSettingEvent;
    type: SupplierNotificationSettingType;
  }[];
};

export function SupplierNotificationEmailList({
  supplier,
  className,
}: {
  supplier: SupplierRecord;
  className?: string;
}) {
  const sections = useSupplierNotificationEvents();

  const eventLabelMap = useMemo(() => {
    const map = new Map<SupplierNotificationSettingEvent, string>();
    sections.forEach((section) =>
      section.events.forEach((e) => map.set(e.event, e.label)),
    );
    return map;
  }, [sections]);

  const groups: EmailGroup[] = useMemo(() => {
    const map = new Map<string, EmailGroup>();
    (supplier.notificationSettings ?? []).forEach((setting) => {
      if (!map.has(setting.email)) {
        map.set(setting.email, { email: setting.email, entries: [] });
      }
      map.get(setting.email)!.entries.push({
        id: setting.id,
        event: setting.event,
        type: setting.type,
      });
    });
    return Array.from(map.values()).sort((a, b) =>
      a.email.localeCompare(b.email),
    );
  }, [supplier.notificationSettings]);

  return (
    <div className={className}>
      {groups.length === 0 ? (
        <EmptyState />
      ) : (
        <div className="space-y-3">
          {groups.map((group) => (
            <EmailRow
              key={group.email}
              group={group}
              supplierId={supplier.id}
              labelFor={(event) => eventLabelMap.get(event) ?? event}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export function SupplierNotificationAddRecipientButton({
  supplierId,
}: {
  supplierId: string;
}) {
  const { open } = useDialog(SupplierNotificationEmailDialog);

  return (
    <Button color="blue" onClick={() => open({ supplierId })}>
      <PlusIcon />
      Add recipient
    </Button>
  );
}

function EmailRow({
  group,
  supplierId,
  labelFor,
}: {
  group: EmailGroup;
  supplierId: string;
  labelFor: (event: SupplierNotificationSettingEvent) => string;
}) {
  const { open } = useDialog(SupplierNotificationEmailDialog);
  const { open: openConfirm } = useDialog(ConfirmDeleteDialog);
  const { mutateAsync } = useGqlMutation(updateSupplierMutation);

  return (
    <div className="flex flex-col gap-3 rounded-lg border border-gray-200 bg-white p-4 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex items-start gap-3">
        <div className="rounded-lg border border-blue-200 bg-blue-50 p-2 text-blue-500">
          <MailIcon className="h-4 w-4" />
        </div>
        <div className="min-w-0">
          <div className="truncate text-sm font-medium text-gray-900">
            {group.email}
          </div>
          <div className="mt-1 flex flex-wrap gap-1">
            {group.entries.slice(0, MAX_VISIBLE_BADGES).map((entry) => (
              <Badge key={entry.id} color="zinc">
                {labelFor(entry.event)}
                {entry.type !== SupplierNotificationSettingType.To ? (
                  <span className="ml-1 text-[10px] uppercase opacity-70">
                    {entry.type}
                  </span>
                ) : null}
              </Badge>
            ))}
            {group.entries.length > MAX_VISIBLE_BADGES ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Badge color="zinc">
                    +{group.entries.length - MAX_VISIBLE_BADGES} more
                  </Badge>
                </TooltipTrigger>
                <TooltipContent>
                  <ul className="space-y-0.5">
                    {group.entries.slice(MAX_VISIBLE_BADGES).map((entry) => (
                      <li key={entry.id}>
                        {labelFor(entry.event)}
                        {entry.type !== SupplierNotificationSettingType.To
                          ? ` (${entry.type})`
                          : null}
                      </li>
                    ))}
                  </ul>
                </TooltipContent>
              </Tooltip>
            ) : null}
          </div>
        </div>
      </div>
      <div className="flex shrink-0 gap-2">
        <Button
          outline
          onClick={() => open({ supplierId, email: group.email })}
        >
          <PencilIcon data-slot="icon" />
          Edit
        </Button>
        <Button
          outline
          onClick={() =>
            openConfirm({
              title: `Remove ${group.email}?`,
              message:
                "This recipient will stop receiving all event notifications.",
              onDelete: async () => {
                await mutateAsync({
                  id: supplierId,
                  input: {
                    notificationSettings: {
                      upsert: [],
                      delete: group.entries.map((e) => e.id),
                    },
                  },
                });
              },
            })
          }
        >
          <TrashIcon data-slot="icon" />
          Remove
        </Button>
      </div>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-gray-300 bg-white px-6 py-12 text-center">
      <MailIcon className="h-8 w-8 text-gray-400" />
      <h3 className="mt-3 text-sm font-medium text-gray-900">
        No recipients yet
      </h3>
      <p className="mt-1 text-sm text-gray-500">
        Add an email to start receiving event notifications
      </p>
    </div>
  );
}
